import { useEffect, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { History, ClipboardPaste, MessageCircle, Plus, X, RotateCcw, Scale, Printer, FileSpreadsheet, AlertTriangle, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { printFreightMaster, printRateCompare, printRateTalkPack } from "@/lib/print";
import { inr, today, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const UNITS = ["Per Trip", "Per MT", "Per KG", "Per Package"];
const OFFICES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
const VEHICLE_TYPES = [
  "Bollero pick up", "14 Feet", "17 Feet", "19 Feet", "20 Feet", "22 Feet",
  "10 Feet container", "20 Feet container", "24 Feet container",
  "32 Feet SXL container", "32 Feet MXL container",
  "20 Feet Trailer", "40 Feet Trailer", "40 Feet Low Bed Trailer", "40 Feet Semi Low Bed Trailer",
];

function FreightBranchChips({ rows }) {
  if (!rows || !rows.length) return null;
  const counts = {};
  rows.forEach((r) => {
    const b = String(r.branch || "").trim() || "No Branch";
    counts[b] = (counts[b] || 0) + 1;
  });
  const keys = [...OFFICES.filter((b) => counts[b]), ...Object.keys(counts).filter((k) => !OFFICES.includes(k)).sort()];
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="freight-branch-chips">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rates by branch:</span>
      {keys.map((b) => (
        <span
          key={b}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${b === "No Branch" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}
          data-testid="freight-branch-chip"
        >
          {b} <span className="rounded-full bg-white px-1.5 font-bold">{counts[b]}</span>
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-xs font-bold text-primary" data-testid="freight-branch-total">
        Total {rows.length}
      </span>
    </div>
  );
}

const exportFreightCsv = (list) => {
  const headers = ["Sl No.", "Customer Name", "Customer Code", "Branch", "From", "Destination", "Freight Charge", "Loading Charge", "Unloading Charge", "Vehicle Type", "Payment Terms", "Valid From", "Valid Till", "Remarks"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.customer_name || "", r.customer_code || "", r.branch || "", r.origin || "", r.destination || "", r.freight_amount || 0, r.loading_charge || 0, r.unloading_charge || 0, r.unit || "", r.payment_terms || "", r.valid_from || "", r.valid_till || "", r.remarks || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-freight-master-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const normPasteDate = (s) => {
  s = String(s || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return s;
};

const DITTO = /^("|''|-?do-?)$/i;

const parseBulkRates = (text) => {
  const out = [];
  let prev = null;
  const isDate = (s) => /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(String(s || "").trim()) || /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
  String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (l.includes("\t") ? l.split("\t") : l.split(",")))
    .filter((c) => !/^customer/i.test((c[0] || "").trim()))
    .forEach((c) => {
      const cells = c.map((x) => (x || "").trim());
      let r;
      if (cells.length <= 2 && prev) {
        r = { ...prev, destination: cells[0] || "", freight_amount: Number(String(cells[1] || "").replace(/[^0-9.]/g, "")) || 0 };
      } else {
        const inherit = (v, key) => (prev && (!v || DITTO.test(v)) ? prev[key] : DITTO.test(v) ? "" : v);
        const hasTerms = !isDate(cells[6]);
        const off = hasTerms ? 1 : 0;
        r = {
          customer_name: inherit(cells[0], "customer_name") || "",
          branch: inherit(cells[1], "branch") || "",
          origin: inherit(cells[2], "origin") || "",
          destination: cells[3] || "",
          freight_amount: Number(String(cells[4] || "").replace(/[^0-9.]/g, "")) || 0,
          unit: inherit(cells[5], "unit") || "",
          payment_terms: hasTerms ? inherit(cells[6], "payment_terms") || "" : "",
          valid_from: normPasteDate(cells[6 + off]) || (prev && !cells[6 + off] ? prev.valid_from : ""),
          valid_till: normPasteDate(cells[7 + off]) || (prev && !cells[7 + off] ? prev.valid_till : ""),
          remarks: cells[8 + off] || "",
        };
      }
      if (r.customer_name && r.freight_amount > 0) {
        out.push(r);
        prev = r;
      }
    });
  return out;
};

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const parsed = parseBulkRates(text);
  const upload = async () => {
    setBusy(true);
    try {
      const res = await api.post("/freight-master/bulk", { items: parsed });
      toast.success(`${res.data.created} freight rate(s) uploaded`);
      setText("");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="freight-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Paste Freight Rates from Excel</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Copy rows from Excel and paste below. Column order: <b>Customer Name, Branch, From, Destination, Freight Charge, Vehicle Type, Payment Terms, Valid From, Valid Till, Remarks</b> (Payment Terms may be omitted — dates are detected automatically). <b>Repeated customer shortcut:</b> leave Customer/Branch/From blank (or type ") on following rows to repeat the row above — or paste just two columns <b>Destination, Freight</b> to add more destinations for the previous customer. Dates accept DD-MM-YYYY or YYYY-MM-DD.
        </p>
        <Textarea
          rows={8}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"VIRCHOW DRUGS LIMITED\tChennai\tSRICITY\tCOIMBATORE\t45000\t32 Feet SXL container\t01-04-2026\t31-03-2027\tFY 26-27 contract"}
          data-testid="freight-bulk-textarea"
        />
        {text.trim() && (
          <div className="max-h-56 overflow-auto rounded-md border border-border">
            <table className="w-full text-xs" data-testid="freight-bulk-preview">
              <thead className="bg-slate-50">
                <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                  <th>Customer</th><th>Branch</th><th>From</th><th>Destination</th><th className="text-right">Freight</th><th>Vehicle Type</th><th>Payment Terms</th><th>Validity</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((r, i) => (
                  <tr key={i} className="border-t [&>td]:px-2 [&>td]:py-1.5">
                    <td>{r.customer_name}</td><td>{r.branch ? r.branch : <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-700">No Branch</span>}</td><td>{r.origin}</td><td>{r.destination}</td>
                    <td className="text-right tabular-nums">{inr(r.freight_amount)}</td><td>{r.unit || "—"}</td><td>{r.payment_terms || "—"}</td>
                    <td className="whitespace-nowrap">{r.valid_from || r.valid_till ? `${r.valid_from || "—"} → ${r.valid_till || "open"}` : "Always"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <span className="mr-auto self-center text-xs text-muted-foreground">{parsed.length} valid row(s)</span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={upload} disabled={busy || !parsed.length} data-testid="freight-bulk-upload-btn">
            {busy ? "Uploading…" : `Upload ${parsed.length} rate(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_STYLE = {
  Created: "bg-emerald-100 text-emerald-700",
  Updated: "bg-amber-100 text-amber-700",
  Deleted: "bg-red-100 text-red-700",
};

function RateHistoryDialog({ open, onOpenChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/freight-master/history").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="freight-history-dialog">
        <DialogHeader>
          <DialogTitle>Freight Rate Change History</DialogTitle>
        </DialogHeader>
        {items === null ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No rate changes recorded yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs" data-testid="freight-history-table">
              <thead className="bg-slate-50">
                <tr className="[&>th]:whitespace-nowrap [&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                  <th>Date</th><th>Action</th><th>Customer</th><th>Route</th><th className="text-right">Old Rate</th><th className="text-right">New Rate</th><th>Changed By</th>
                </tr>
              </thead>
              <tbody>
                {items.map((h) => (
                  <tr key={h.id} className="border-t [&>td]:px-3 [&>td]:py-2" data-testid="freight-history-row">
                    <td className="whitespace-nowrap">{fmtDate(h.changed_at)}</td>
                    <td><Badge className={`${ACTION_STYLE[h.action] || ""} border-0 text-[10px]`}>{h.action}</Badge></td>
                    <td>{h.customer_name || "—"}</td>
                    <td className="whitespace-nowrap">{h.origin || "—"} → {h.destination || "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{h.old_amount != null ? `${inr(h.old_amount)} ${h.old_unit || ""}` : "—"}</td>
                    <td className="whitespace-nowrap text-right tabular-nums font-medium">{h.new_amount != null ? `${inr(h.new_amount)} ${h.new_unit || ""}` : "—"}</td>
                    <td>{h.changed_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LowRateDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) api.get("/reports/low-rate").then((r) => setRows(r.data || [])).catch(() => {});
  }, [open]);
  const months = {};
  rows.forEach((r) => {
    const k = r.month || "—";
    (months[k] = months[k] || []).push(r);
  });
  const keys = Object.keys(months).sort().reverse();
  const totalLeak = rows.reduce((a, r) => a + (r.leak || 0), 0);
  const exportCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [["Month", "LR No.", "LR Date", "Customer", "From", "Destination", "Billed Freight", "Rate Card", "Leaked ₹"].join(",")];
    rows.forEach((r) => lines.push([r.month, r.lr_no, r.date, r.customer_name, r.origin, r.destination, r.billed, r.card_rate, r.leak].map(cell).join(",")));
    lines.push(["TOTAL", "", "", "", "", "", "", "", totalLeak].join(","));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `NTC-Low-Rate-Report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const monthLabel = (m) => {
    const [y, mo] = String(m).split("-");
    return mo ? new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) : m;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[760px]" data-testid="low-rate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Low Rate Report — Billed Below Rate Card</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <span className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-bold text-red-700" data-testid="low-rate-total">
            Total leaked: {inr(totalLeak)} · {rows.length} consignment(s)
          </span>
          <Button size="sm" variant="outline" className="ml-auto" disabled={!rows.length} onClick={exportCsv} data-testid="low-rate-excel-btn">
            <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Excel
          </Button>
        </div>
        {keys.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No consignments billed below the rate card. All lanes hold their agreed rates.</p>
        ) : (
          keys.map((m) => {
            const list = months[m];
            const mLeak = list.reduce((a, r) => a + (r.leak || 0), 0);
            return (
              <div key={m} className="rounded-md border border-border" data-testid="low-rate-month-block">
                <div className="flex items-center justify-between border-b bg-slate-50 px-3 py-2">
                  <span className="text-sm font-bold">{monthLabel(m)}</span>
                  <span className="text-xs font-bold text-red-600">Leaked {inr(mLeak)} · {list.length} LR(s)</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="[&>th]:px-3 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                      <th>LR No.</th><th>Date</th><th>Customer</th><th>Route</th><th className="text-right">Billed</th><th className="text-right">Rate Card</th><th className="text-right">Leaked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r, i) => (
                      <tr key={i} className="border-t [&>td]:px-3 [&>td]:py-1.5" data-testid="low-rate-row">
                        <td className="font-semibold">{r.lr_no || "—"}</td>
                        <td className="whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
                        <td>{r.customer_name}</td>
                        <td>{r.origin} → {r.destination}</td>
                        <td className="whitespace-nowrap text-right tabular-nums">{inr(r.billed)}</td>
                        <td className="whitespace-nowrap text-right tabular-nums">{inr(r.card_rate)}</td>
                        <td className="whitespace-nowrap text-right font-bold tabular-nums text-red-600">{inr(r.leak)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </DialogContent>
    </Dialog>
  );
}

function RateCompareDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [route, setRoute] = useState("All Routes");
  useEffect(() => {
    if (!open) return;
    setRoute("All Routes");
    api.get("/freight-master").then((r) => setRows(r.data || [])).catch(() => {});
  }, [open]);
  const routeOf = (r) => `${(r.origin || "—").toUpperCase().trim()} → ${(r.destination || "—").toUpperCase().trim()}`;
  const groups = {};
  rows.forEach((r) => {
    const k = routeOf(r);
    (groups[k] = groups[k] || []).push(r);
  });
  const routes = Object.keys(groups).sort();
  const shown = route === "All Routes" ? routes : routes.filter((k) => k === route);
  const sections = shown.map((k) => {
    const list = [...groups[k]].sort((a, b) => (Number(a.freight_amount) || 0) - (Number(b.freight_amount) || 0));
    return { route: k, rows: list, min: Number(list[0]?.freight_amount) || 0 };
  });
  const exportCompareCsv = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [["Route", "Customer", "Vehicle Type", "Freight", "vs Lowest %", "Loading Charge", "Unloading Charge", "Valid Till", "Lowest"].join(",")];
    sections.forEach((s) =>
      s.rows.forEach((r) => {
        const amt = Number(r.freight_amount) || 0;
        const diff = s.min ? Math.round(((amt - s.min) / s.min) * 100) : 0;
        lines.push([s.route, r.customer_name || "", r.unit || "", amt, amt === s.min ? 0 : diff, r.loading_charge || 0, r.unloading_charge || 0, r.valid_till || "", amt === s.min && s.rows.length > 1 ? "LOWEST" : ""].map(cell).join(","));
      })
    );
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `NTC-Rate-Compare-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const waCompare = () => {
    const lines = ["*NTC — Route Rate Comparison*", ""];
    sections.forEach((s) => {
      lines.push(`*${s.route}* — ${s.rows.length} rate(s)`);
      s.rows.forEach((r) => {
        const amt = Number(r.freight_amount) || 0;
        const diff = s.min ? Math.round(((amt - s.min) / s.min) * 100) : 0;
        lines.push(`• ${r.customer_name || "—"}: ₹${amt.toLocaleString("en-IN")}${amt === s.min && s.rows.length > 1 ? " (LOWEST)" : diff ? ` (+${diff}%)` : ""}${r.unit ? ` — ${r.unit}` : ""}`);
      });
      lines.push("");
    });
    lines.push("For rate negotiation reference — Nisha Transport Corporation");
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[760px]" data-testid="rate-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Rate Compare — Route-wise</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Route</span>
          <Select value={route} onValueChange={setRoute}>
            <SelectTrigger className="h-9 w-[280px] text-sm" data-testid="compare-route-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All Routes", ...routes].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="ml-auto" disabled={!sections.length} onClick={() => printRateCompare(sections)} data-testid="compare-print-btn">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
          </Button>
          <Button size="sm" variant="outline" disabled={!sections.length} onClick={exportCompareCsv} data-testid="compare-excel-btn">
            <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Excel
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-emerald-300 bg-emerald-50 font-semibold text-emerald-700 hover:bg-emerald-100"
            disabled={!sections.length}
            onClick={waCompare}
            data-testid="compare-wa-btn"
          >
            <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
          </Button>
        </div>
        {shown.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No freight rates to compare yet.</p>
        ) : (
          shown.map((k) => {
            const list = [...groups[k]].sort((a, b) => (Number(a.freight_amount) || 0) - (Number(b.freight_amount) || 0));
            const min = Number(list[0]?.freight_amount) || 0;
            return (
              <div key={k} className="rounded-md border border-border" data-testid="compare-route-block">
                <div className="flex items-center justify-between border-b bg-slate-50 px-3 py-2">
                  <span className="text-sm font-bold">{k}</span>
                  <span className="text-xs text-muted-foreground">{list.length} customer rate(s)</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="[&>th]:px-3 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                      <th>Customer</th><th>Vehicle Type</th><th className="text-right">Freight</th><th className="text-right">vs Lowest</th><th>Validity</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => {
                      const amt = Number(r.freight_amount) || 0;
                      const diff = min ? Math.round(((amt - min) / min) * 100) : 0;
                      const expired = r.valid_till && r.valid_till < today();
                      return (
                        <tr key={r.id} className={`border-t [&>td]:px-3 [&>td]:py-1.5 ${amt === min ? "bg-amber-50/70" : ""}`} data-testid="compare-rate-row">
                          <td className="font-semibold">{r.customer_name || "—"}</td>
                          <td>{r.unit || "—"}</td>
                          <td className="whitespace-nowrap text-right font-bold tabular-nums">{inr(amt)}</td>
                          <td className="whitespace-nowrap text-right tabular-nums">{amt === min ? "—" : `+${diff}%`}</td>
                          <td className="whitespace-nowrap">{r.valid_till ? `till ${fmtDate(r.valid_till)}` : "Always"}{expired ? " · Expired" : ""}</td>
                          <td>
                            {amt === min && list.length > 1 && (
                              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="compare-lowest-badge">Lowest — review pricing</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FreightMaster() {
  const [custs, setCusts] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [lowRateOpen, setLowRateOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    api.get("/customers").then((r) => setCusts((r.data || []).filter((c) => c.name))).catch(() => {});
  }, []);
  const customers = custs.map((c) => c.name).sort();
  const codeOf = (name) => custs.find((c) => c.name === name)?.customer_code || "";
  const enrich = (r) => ({ ...r, customer_code: r.customer_code || codeOf(r.customer_name) });

  const gatherTalk = async (name) => {
    const [recRes, outRes, subRes] = await Promise.all([
      api.get("/receipts"),
      api.get("/reports/outstanding"),
      api.get("/bill-submissions"),
    ]);
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    const cut = cutoff.toISOString().slice(0, 10);
    let received = 0, deducted = 0, bounces = 0;
    const monthsDed = {};
    (recRes.data || []).forEach((r) => {
      if (r.reversed || (r.customer_name || "") !== name || String(r.date || "") < cut) return;
      received += Number(r.amount) || 0;
      if (r.cheque_status === "Bounced") bounces += 1;
      if ((r.deduction_status || "") !== "Rejected") {
        const d = (Number(r.deduction) || 0) + (r.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
        if (d > 0) {
          deducted += d;
          const m = String(r.date || "").slice(0, 7);
          monthsDed[m] = (monthsDed[m] || 0) + d;
        }
      }
    });
    const outstanding = (outRes.data?.rows || []).filter((r) => r.customer_name === name).reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
    const t = today();
    const brokenPromises = (subRes.data || [])
      .filter((s) => s.customer_name === name && !["Cleared", "Returned"].includes(s.status))
      .reduce((n, s) => n + (s.call_logs || []).filter((l) => ["Payment Promised", "Cheque Ready"].includes(l.outcome) && (l.next_call_date || l.date) && (l.next_call_date || l.date) < t).length, 0);
    const rates = allRates.filter((r) => r.customer_name === name).map(enrich);
    return { rates, stats: { received, deducted, dedPct: received + deducted > 0 ? (deducted / (received + deducted)) * 100 : 0, bounces, brokenPromises, outstanding, monthsDed } };
  };

  const talkPack = async (row) => {
    const name = row.customer_name;
    try {
      const { rates, stats } = await gatherTalk(name);
      printRateTalkPack({ name, customer_code: codeOf(name) }, rates, stats);
    } catch {
      toast.error("Could not load data for the talk pack");
    }
  };

  const talkPackWa = async (row) => {
    const name = row.customer_name;
    try {
      const { rates, stats } = await gatherTalk(name);
      const msg = [
        "*NTC — Rate Talk Summary (before meeting)*",
        "",
        `Customer: ${name}${codeOf(name) ? ` (${codeOf(name)})` : ""}`,
        "",
        "*Current Rates:*",
        ...rates.map((r, i) => `${i + 1}. ${r.origin || "—"} → ${r.destination || "—"}${r.unit ? ` (${r.unit})` : ""} — ₹${(Number(r.freight_amount) || 0).toLocaleString("en-IN")}${r.payment_terms ? ` · ${r.payment_terms}` : ""}`),
        "",
        "*Payment habits (6 mo):*",
        `Received: ${inr(stats.received)}`,
        `Outstanding now: ${inr(stats.outstanding)}`,
        `Deducted: ${inr(stats.deducted)} (${stats.dedPct.toFixed(1)}%)`,
        `Bounced cheques: ${stats.bounces} · Broken promises: ${stats.brokenPromises}`,
        "",
        "Suggest factoring the above into the revised rates.",
      ].join("\n");
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Could not load data for the talk summary");
    }
  };

  const expiringSoon = (r) => r.valid_till && (new Date(r.valid_till) - new Date(today())) / 86400000 <= 15;
  const [renewTarget, setRenewTarget] = useState(null);
  const [hike, setHike] = useState("");
  const [receiptsData, setReceiptsData] = useState(null);
  useEffect(() => {
    if (renewTarget && receiptsData === null) {
      api.get("/receipts").then((r) => setReceiptsData(r.data || [])).catch(() => setReceiptsData([]));
    }
  }, [renewTarget, receiptsData]);
  const dedStats = (custName) => {
    if (!receiptsData || !custName) return null;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    const cut = cutoff.toISOString().slice(0, 10);
    let received = 0, deducted = 0;
    receiptsData.forEach((r) => {
      if (r.reversed || (r.customer_name || "") !== custName || String(r.date || "") < cut) return;
      received += Number(r.amount) || 0;
      if ((r.deduction_status || "") !== "Rejected") {
        deducted += (Number(r.deduction) || 0) + (r.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
      }
    });
    if (received + deducted <= 0) return null;
    return { deducted, pct: (deducted / (received + deducted)) * 100 };
  };
  const DedChip = ({ name }) => {
    const s = dedStats(name);
    if (!s) return null;
    if (s.deducted <= 0)
      return <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="renew-ded-chip">No deductions (6 mo)</span>;
    const clr = s.pct >= 3 ? "bg-rose-50 text-rose-700" : s.pct >= 1 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
    return (
      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${clr}`} title="Deduction history — last 6 months" data-testid="renew-ded-chip">
        Deducts {s.pct.toFixed(1)}% · {inr(s.deducted)} (6 mo)
      </span>
    );
  };
  const [allRates, setAllRates] = useState([]);
  useEffect(() => {
    api.get("/freight-master").then((r) => setAllRates(r.data || [])).catch(() => {});
  }, [reloadKey]);
  const laneSuggestion = (form) => {
    const norm = (s) => String(s || "").trim().toUpperCase();
    if (!norm(form.origin) || !norm(form.destination)) return null;
    const lane = allRates.filter(
      (r) => norm(r.origin) === norm(form.origin) && norm(r.destination) === norm(form.destination) && norm(r.customer_name) !== norm(form.customer_name) && Number(r.freight_amount) > 0
    );
    if (!lane.length) return null;
    const amts = lane.map((r) => Number(r.freight_amount));
    return { count: lane.length, avg: Math.round(amts.reduce((a, b) => a + b, 0) / amts.length), min: Math.min(...amts), max: Math.max(...amts) };
  };
  const postRenew = async (r, pct = 0) => {
    const from = today();
    const d = new Date(from);
    d.setFullYear(d.getFullYear() + 1);
    const till = d.toISOString().slice(0, 10);
    const newAmt = Math.round((Number(r.freight_amount) || 0) * (1 + (Number(pct) || 0) / 100));
    await api.post("/freight-master", {
      customer_name: r.customer_name,
      branch: r.branch || "",
      origin: r.origin || "",
      destination: r.destination || "",
      freight_amount: newAmt,
      loading_charge: r.loading_charge || 0,
      unloading_charge: r.unloading_charge || 0,
      unit: r.unit || "",
      payment_terms: r.payment_terms || "",
      customer_code: r.customer_code || "",
      valid_from: from,
      valid_till: till,
      remarks: r.remarks || "",
    });
    return { from, till, newAmt };
  };
  const renewRate = async (r, pct = 0) => {
    try {
      const oldAmt = Number(r.freight_amount) || 0;
      const { from, till, newAmt } = await postRenew(r, pct);
      toast.success(`Rate renewed at ₹${newAmt.toLocaleString("en-IN")}${Number(pct) ? ` (+${pct}%)` : ""}: valid ${fmtDate(from)} → ${fmtDate(till)}.`);
      setReloadKey((k) => k + 1);
      const cust = custs.find((c) => c.name === r.customer_name);
      const msg = [
        "*Nisha Transport Corporation — Freight Rate Renewed*",
        "",
        `Customer: ${r.customer_name}${cust?.customer_code ? ` (${cust.customer_code})` : ""}`,
        `Route: ${r.origin || "—"} → ${r.destination || "—"}`,
        `Freight Charge: ₹${newAmt.toLocaleString("en-IN")}${r.unit ? ` (${r.unit})` : ""}${Number(pct) ? ` — revised +${pct}% from ₹${oldAmt.toLocaleString("en-IN")}` : ""}`,
        r.payment_terms ? `Payment Terms: ${r.payment_terms}` : "",
        `Valid: ${fmtDate(from)} → ${fmtDate(till)}`,
        "",
        "Thank you for your continued business!",
      ].filter((l) => l !== "").join("\n");
      const ph = String(cust?.phone || "").replace(/\D/g, "");
      window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const renewMany = async (rows, pct = 0) => {
    let ok = 0;
    for (const r of rows) {
      try {
        await postRenew(r, pct);
        ok += 1;
      } catch {
        /* skip failed */
      }
    }
    toast.success(`${ok} rate(s) renewed${Number(pct) ? ` with +${pct}% hike` : ""} — valid 1 year from today.`);
    setReloadKey((k) => k + 1);
  };

  const waRateCard = async (row) => {
    try {
      const res = await api.get("/freight-master");
      const todayS = today();
      const rates = (res.data || []).filter((r) => r.customer_name === row.customer_name && !(r.valid_till && r.valid_till < todayS));
      if (!rates.length) {
        toast.error("No active rates for this customer");
        return;
      }
      const cust = custs.find((c) => c.name === row.customer_name);
      const lines = rates.map(
        (r, i) =>
          `${i + 1}. ${r.origin || "—"} → ${r.destination || "—"}: ₹${Number(r.freight_amount || 0).toLocaleString("en-IN")}${r.unit ? ` (${r.unit})` : ""}${r.loading_charge ? ` + Loading ₹${Number(r.loading_charge).toLocaleString("en-IN")}` : ""}${r.unloading_charge ? ` + Unloading ₹${Number(r.unloading_charge).toLocaleString("en-IN")}` : ""}${r.valid_till ? ` — valid till ${fmtDate(r.valid_till)}` : ""}`
      );
      const msg = `*Nisha Transport Corporation — Agreed Freight Rate Card*\n\nCustomer: ${row.customer_name}${cust?.customer_code ? ` (${cust.customer_code})` : ""}${(rates.find((r) => r.payment_terms)?.payment_terms) ? `\nPayment Terms: ${rates.find((r) => r.payment_terms).payment_terms}` : ""}\n\n${lines.join("\n")}\n\nFor bookings, please contact your NTC branch. Thank you!`;
      const ph = String(cust?.phone || "").replace(/[^\d]/g, "");
      window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Could not load rates");
    }
  };
  return (
    <>
      <EntityManager
        key={reloadKey}
        title="Freight Master"
        subtitle="Customer-wise agreed freight rates by route — so staff always bill the correct charge."
        endpoint="freight-master"
        banner={(rows) => <FreightBranchChips rows={rows} />}
        entityName="Freight Rate"
        pageSize={20}
        windowControls
        selectable
        onPrintSelected={(list) => printFreightMaster(list.map(enrich))}
        selectedActions={(sel) =>
          sel.length ? (
            <Button
              variant="outline"
              className="border-amber-300 bg-amber-50 font-semibold text-amber-700 hover:bg-amber-100"
              onClick={() => {
                setHike("");
                setRenewTarget(sel);
              }}
              data-testid="bulk-renew-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Renew ({sel.length})
            </Button>
          ) : null
        }
        exportCsv={(list) => exportFreightCsv(list.map(enrich))}
        rowActions={(r) =>
          expiringSoon(r) ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1 border-amber-300 bg-amber-50 px-2 text-xs font-semibold text-amber-700 hover:bg-amber-100"
              title="Copy this rate with fresh 1-year validity from today"
              onClick={() => {
                setHike("");
                setRenewTarget(r);
              }}
              data-testid="freight-renew-btn"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Renew
            </Button>
          ) : null
        }
        headerExtra={() => (
          <>
            {isAdmin && (
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="freight-bulk-btn">
                <ClipboardPaste className="mr-2 h-4 w-4" /> Paste from Excel
              </Button>
            )}
            <Button variant="outline" onClick={() => setCompareOpen(true)} data-testid="freight-compare-btn">
              <Scale className="mr-2 h-4 w-4" /> Compare Routes
            </Button>
            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setLowRateOpen(true)} data-testid="low-rate-report-btn">
              <AlertTriangle className="mr-2 h-4 w-4" /> Low Rate Report
            </Button>
            <Button variant="outline" onClick={() => setHistOpen(true)} data-testid="freight-history-btn">
              <History className="mr-2 h-4 w-4" /> Rate History
            </Button>
          </>
        )}
        defaults={{
          customer_name: "",
          customer_code: "",
          branch: "",
          origin: "",
          destination: "",
          freight_amount: "",
          loading_charge: "",
          unloading_charge: "",
          unit: "",
          payment_terms: "",
          valid_from: "",
          valid_till: "",
          remarks: "",
          extra_destinations: [],
        }}
        dialogClassName="sm:max-w-[920px]"
        formExtraAfter="freight_amount"
        formExtra={(form, setForm) => {
          if (form.id) return null;
          const sug = laneSuggestion(form);
          return (
            <>
              {sug && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50/80 px-3 py-2 text-xs text-sky-800" data-testid="freight-rate-suggest">
                  <span>
                    <b>Fair rate hint:</b> {sug.count} other customer rate(s) on this lane average <b>{inr(sug.avg)}</b>
                    {sug.min !== sug.max ? ` (₹${sug.min.toLocaleString("en-IN")} – ₹${sug.max.toLocaleString("en-IN")})` : ""}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 border-sky-300 bg-white px-2 text-[11px] font-bold text-sky-700 hover:bg-sky-100"
                    onClick={() => setForm({ ...form, freight_amount: sug.avg })}
                    data-testid="freight-suggest-use-btn"
                  >
                    Use {inr(sug.avg)}
                  </Button>
                </div>
              )}
              {sug && Number(form.freight_amount) > 0 && Number(form.freight_amount) < sug.avg * 0.9 && (
                <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="freight-low-rate-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    Low rate warning: {inr(form.freight_amount)} is <b>{Math.round((1 - Number(form.freight_amount) / sug.avg) * 100)}% below</b> the lane average {inr(sug.avg)} — check for underpricing before saving.
                  </span>
                </div>
              )}
            </>
          );
        }}
        formSide={(form, setForm) => {
          if (form.id) return null;
          return (
              <div className="grid gap-2 rounded-md border border-dashed border-border bg-slate-50/60 p-3" data-testid="freight-multi-dest-section">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Multiple Destination (optional)</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={() => setForm({ ...form, extra_destinations: [...(form.extra_destinations || []), { destination: "", freight_amount: "", unit: "" }] })}
                  data-testid="freight-add-dest-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Add Destination
                </Button>
              </div>
              {(form.extra_destinations || []).length > 0 && (
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  <span className="w-6 shrink-0">Sl</span>
                  <span className="flex-1">Destination</span>
                  <span className="w-40 shrink-0">Vehicle Type</span>
                  <span className="w-28 shrink-0">Freight ₹</span>
                  <span className="w-8 shrink-0" />
                </div>
              )}
              {(form.extra_destinations || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2" data-testid="freight-extra-dest-row">
                  <span className="w-6 shrink-0 text-center text-xs font-bold text-slate-500" data-testid={`freight-extra-sl-${i}`}>{i + 2}</span>
                  <Input
                    placeholder={`Destination ${i + 2}`}
                    value={d.destination}
                    onChange={(e) => {
                      const list = [...form.extra_destinations];
                      list[i] = { ...list[i], destination: e.target.value };
                      setForm({ ...form, extra_destinations: list });
                    }}
                    data-testid={`freight-extra-dest-${i}`}
                  />
                  <Input
                    placeholder="Vehicle Type"
                    className="w-40 shrink-0"
                    list="extra-vt-suggestions"
                    value={d.unit || ""}
                    onChange={(e) => {
                      const list = [...form.extra_destinations];
                      list[i] = { ...list[i], unit: e.target.value };
                      setForm({ ...form, extra_destinations: list });
                    }}
                    data-testid={`freight-extra-unit-${i}`}
                  />
                  <Input
                    type="number"
                    placeholder="Freight ₹"
                    className="w-28 shrink-0"
                    value={d.freight_amount}
                    onChange={(e) => {
                      const list = [...form.extra_destinations];
                      list[i] = { ...list[i], freight_amount: e.target.value };
                      setForm({ ...form, extra_destinations: list });
                    }}
                    data-testid={`freight-extra-amount-${i}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-rose-600"
                    onClick={() => setForm({ ...form, extra_destinations: form.extra_destinations.filter((_, j) => j !== i) })}
                    data-testid={`freight-remove-dest-${i}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(form.extra_destinations || []).length > 0 && (
                <>
                  <datalist id="extra-vt-suggestions">
                    {[...VEHICLE_TYPES, ...UNITS].map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                  <p className="text-[11px] text-muted-foreground">Each destination saves as a separate rate with the same customer, branch, from and validity. Leave Vehicle Type blank to use the main one below.</p>
                </>
              )}
            </div>
          );
        }}
        fields={[
          { key: "customer_name", label: "Customer Name", type: "lookup", options: customers.map((n) => ({ value: n, label: n })), applyPatch: (opt) => ({ customer_code: codeOf(opt?.value) || "" }), placeholder: "Select customer" },
          { key: "customer_code", label: "Customer Code", type: "text", placeholder: "Auto-fills from customer — e.g. N1004" },
          { key: "branch", label: "Branch", type: "select", options: OFFICES, placeholder: "Select branch" },
          { key: "origin", label: "From (Origin)", type: "text", placeholder: "SRICITY" },
          { key: "destination", label: "Destination", type: "text", placeholder: "COIMBATORE" },
          { key: "freight_amount", label: "Freight Charge (₹)", type: "number", placeholder: "45000" },
          { key: "loading_charge", label: "Loading Charge (₹, optional)", type: "number", placeholder: "e.g. 1500" },
          { key: "unloading_charge", label: "Unloading Charge (₹, optional)", type: "number", placeholder: "e.g. 1500" },
          { key: "unit", label: "Vehicle Type", type: "text", suggestions: [...VEHICLE_TYPES, ...UNITS], placeholder: "Type any vehicle type or pick from list" },
          { key: "payment_terms", label: "Payment Terms", type: "text", suggestions: ["Advance", "15 Days Credit", "30 Days Credit", "45 Days Credit", "60 Days Credit", "To Pay"], placeholder: "e.g. 30 Days Credit" },
          { key: "valid_from", label: "Valid From (optional)", type: "date" },
          { key: "valid_till", label: "Valid Till (optional)", type: "date" },
          { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Rate valid from Apr 2026" },
        ]}
        columns={[
          { key: "_sl", label: "SL No.", render: (r, idx) => (typeof idx === "number" ? idx + 1 : "—") },
          { key: "branch", label: "Branch", render: (r) => r.branch || "—" },
          { key: "customer_name", label: "Customer Name" },
          { key: "customer_code", label: "Customer Code", render: (r) => <span data-testid="freight-cust-code">{r.customer_code || codeOf(r.customer_name) || "—"}</span> },
          { key: "origin", label: "From" },
          { key: "destination", label: "Destination" },
          { key: "freight_amount", label: "Freight Charge", align: "right", render: (r) => inr(r.freight_amount) },
          { key: "loading_charge", label: "Loading Charge", align: "right", render: (r) => (r.loading_charge ? inr(r.loading_charge) : "—") },
          { key: "unloading_charge", label: "Unloading Charge", align: "right", render: (r) => (r.unloading_charge ? inr(r.unloading_charge) : "—") },
          { key: "unit", label: "Vehicle Type" },
          { key: "payment_terms", label: "Payment Terms", render: (r) => <span data-testid="freight-pay-terms">{r.payment_terms || "—"}</span> },
          {
            key: "validity",
            label: "Validity",
            render: (r) => {
              const from = r.valid_from ? fmtDate(r.valid_from) : "";
              const till = r.valid_till ? fmtDate(r.valid_till) : "";
              const expired = r.valid_till && r.valid_till < today();
              const notStarted = r.valid_from && r.valid_from > today();
              return (
                <span className="whitespace-nowrap text-xs" data-testid="freight-validity">
                  {from || till ? `${from || "—"} → ${till || "open"}` : "Always"}
                  {expired && <Badge className="ml-1.5 border-0 bg-red-100 text-[10px] text-red-700">Expired</Badge>}
                  {notStarted && <Badge className="ml-1.5 border-0 bg-slate-100 text-[10px] text-slate-600">Upcoming</Badge>}
                </span>
              );
            },
          },
          { key: "remarks", label: "Remarks" },
          {
            key: "wa",
            label: "Rate Card",
            render: (r) => (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 border-emerald-300 bg-emerald-50 px-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    waRateCard(r);
                  }}
                  data-testid="freight-wa-btn"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 px-2 text-xs font-semibold text-slate-600"
                  title="Print negotiation sheet: rates + deduction % + payment habits"
                  onClick={(e) => {
                    e.stopPropagation();
                    talkPack(r);
                  }}
                  data-testid="freight-talkpack-btn"
                >
                  <FileText className="h-3.5 w-3.5" /> Talk Pack
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 w-7 p-0 text-sky-600"
                  title="WhatsApp the negotiation summary to the owner before the meeting"
                  onClick={(e) => {
                    e.stopPropagation();
                    talkPackWa(r);
                  }}
                  data-testid="freight-talkpack-wa-btn"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </Button>
              </div>
            ),
          },
        ]}
        filters={[
          { key: "q", label: "Quick Search", type: "text", placeholder: "Customer, route, code, vehicle…", match: (r, v) => !v || `${r.customer_name || ""} ${codeOf(r.customer_name)} ${r.branch || ""} ${r.origin || ""} ${r.destination || ""} ${r.unit || ""} ${r.remarks || ""}`.toLowerCase().includes(String(v).toLowerCase()) },
          { key: "cust", label: "Customer", type: "select", options: ["All Customers", ...customers], match: (r, v) => !v || v === "All Customers" || r.customer_name === v },
          { key: "branchf", label: "Branch", type: "select", options: ["All Branches", ...OFFICES], match: (r, v) => !v || v === "All Branches" || (r.branch || "") === v },
          { key: "expired", label: "Validity", toggleLabel: "Expired only", type: "toggle", match: (r, v) => !v || (r.valid_till && String(r.valid_till).slice(0, 10) < new Date().toISOString().slice(0, 10)), badge: (rows) => rows.filter((r) => r.valid_till && String(r.valid_till).slice(0, 10) < new Date().toISOString().slice(0, 10)).length },
          { key: "nobranch", label: "Branch Gap", toggleLabel: "No Branch only", type: "toggle", match: (r, v) => !v || !String(r.branch || "").trim(), badge: (rows) => rows.filter((r) => !String(r.branch || "").trim()).length },
        ]}
      />
      <RateHistoryDialog open={histOpen} onOpenChange={setHistOpen} />
      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={() => setReloadKey((k) => k + 1)} />
      <RateCompareDialog open={compareOpen} onOpenChange={setCompareOpen} />
      <LowRateDialog open={lowRateOpen} onOpenChange={setLowRateOpen} />
      <Dialog open={!!renewTarget} onOpenChange={(o) => !o && setRenewTarget(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Renew Freight Rate</DialogTitle>
          </DialogHeader>
          {renewTarget && (
            <div className="grid gap-3 py-1">
              {Array.isArray(renewTarget) ? (
                <div className="rounded-md border border-border bg-slate-50 p-3 text-sm" data-testid="bulk-renew-summary">
                  <div className="font-bold">{renewTarget.length} rate(s) selected</div>
                  <div className="mt-1 max-h-32 overflow-y-auto text-xs text-muted-foreground">
                    {renewTarget.map((r) => (
                      <div key={r.id} className="flex flex-wrap items-center gap-x-1.5">
                        <span>{r.customer_name} · {r.origin || "—"} → {r.destination || "—"} · {inr(r.freight_amount)}</span>
                        <DedChip name={r.customer_name} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-1 text-xs">
                    Total current: <b>{inr(renewTarget.reduce((a, r) => a + (Number(r.freight_amount) || 0), 0))}</b>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-border bg-slate-50 p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 font-bold">
                    {renewTarget.customer_name}
                    <DedChip name={renewTarget.customer_name} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {renewTarget.origin || "—"} → {renewTarget.destination || "—"}
                    {renewTarget.unit ? ` · ${renewTarget.unit}` : ""}
                  </div>
                  <div className="mt-1 text-xs">
                    Current charge: <b>{inr(renewTarget.freight_amount)}</b>
                  </div>
                  {dedStats(renewTarget.customer_name)?.deducted > 0 && (
                    <p className="mt-1.5 text-[11px] font-medium text-amber-700" data-testid="renew-ded-hint">
                      This customer deducted {inr(dedStats(renewTarget.customer_name).deducted)} in the last 6 months — factor it into the renewal rate.
                    </p>
                  )}
                </div>
              )}
              <div className="grid gap-1.5">
                <span className="text-sm font-medium">Rate hike % (optional{Array.isArray(renewTarget) ? ", applied to all" : ""})</span>
                <Input type="number" placeholder="e.g. 5" value={hike} onChange={(e) => setHike(e.target.value)} data-testid="renew-hike-input" />
                <p className="text-xs text-muted-foreground">
                  {Array.isArray(renewTarget) ? (
                    <>
                      New total: <b data-testid="renew-new-charge">{inr(renewTarget.reduce((a, r) => a + Math.round((Number(r.freight_amount) || 0) * (1 + (Number(hike) || 0) / 100)), 0))}</b> · each valid {fmtDate(today())} → 1 year. WhatsApp is not auto-sent for bulk renew — use each row's Rate Card button after.
                    </>
                  ) : (
                    <>
                      New charge: <b data-testid="renew-new-charge">{inr(Math.round((Number(renewTarget.freight_amount) || 0) * (1 + (Number(hike) || 0) / 100)))}</b> · valid {fmtDate(today())} → 1 year
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewTarget(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                const t = renewTarget;
                setRenewTarget(null);
                if (Array.isArray(t)) await renewMany(t, hike);
                else await renewRate(t, hike);
              }}
              data-testid="renew-confirm-btn"
            >
              {Array.isArray(renewTarget) ? `Renew ${renewTarget.length} Rate(s)` : "Renew & WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
